import { Injectable, inject } from '@angular/core';
import { LocalStorageClass } from '../../helpers/local-storage-helper';
import { AuthResponse } from '../../types/auth/auth.type';
import { AnalyticsService } from '../shared/analytics.service';

/**
 * Web equivalent of the mobile PostLoginService: finalizes a successful v2
 * authentication. The native post-login sequence (FCM cleanup, push-permission
 * screen, privacy-policy gate) is mobile-only; on web we persist the token and
 * reload so AppInitService bootstraps the session, with the existing
 * TenantSelectionDialog covering multi-tenant selection.
 */
@Injectable({ providedIn: 'root' })
export class PostLoginService {
  private readonly analyticsService = inject(AnalyticsService);

  /**
   * Finalizes a v2 auth response that carries the tenants list inline.
   *
   * - 0/1 tenants → persist the token and reload into the app.
   * - >1 tenants  → returns `true` so the caller can show the
   *   TenantSelectionDialog (which switches tenant with the same token).
   */
  completeLoginWithTenants(auth: AuthResponse): boolean {
    if (auth.tenants.length > 1) {
      return true;
    }

    this.analyticsService.trackEvent('user-logged-in');
    LocalStorageClass.setToken(auth.token, auth.refreshToken);
    if (auth.currentTenant) {
      LocalStorageClass.setTenantIdentifier(auth.currentTenant);
    }
    LocalStorageClass.disableTokenRefreshOnInit();
    this.reload();
    return false;
  }

  /**
   * Finalizes a token-only login (OAuth, registration) where the tenants list
   * isn't returned — persist and reload; AppInitService resolves tenants.
   */
  completeLoginWithToken(token: string, refreshToken?: string): void {
    this.analyticsService.trackEvent('user-logged-in');
    LocalStorageClass.setToken(token, refreshToken);
    LocalStorageClass.disableTokenRefreshOnInit();
    this.reload();
  }

  private reload(): void {
    setTimeout(() => window.location.reload(), 300);
  }
}
